import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	GraphQLErrorMessage,
	Input,
	Label,
	Modal,
	Paragraph,
	Popover,
	TextArea,
} from '@treams/treams-ui';

export default function EditTalentReviewSnapshotModal(props: {
	snapshot: { id: string; title: string; description?: string | null };
	onClose: () => void;
}) {
	const { t } = useTranslation();

	const [update, { loading, error }] = graphql.useUpdateTalentReviewSnapshotMutation();

	const formik = useFormik({
		initialValues: {
			title: props.snapshot.title,
			description: props.snapshot.description ?? '',
		},
		onSubmit: async (values) => {
			try {
				await update({
					variables: {
						input: {
							id: props.snapshot.id,
							title: values.title.trim() || props.snapshot.title,
							description: values.description.trim(),
						},
					},
				});
			} catch {
				// error state is shown below the form
				return;
			}

			props.onClose();
		},
	});

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header title={t('talentReviews.v2.snapshot.edit.title')} onClose={props.onClose} />
				<FormikProvider value={formik}>
					<Modal.Contents>
						<Box mb={4}>
							<Label mb={2}>{t('talentReviews.v2.snapshot.create.nameLabel')}</Label>
							<Input.Formik name="title" />
						</Box>
						<Box mb={4}>
							<Label mb={2}>{t('talentReviews.v2.snapshot.create.descriptionLabel')}</Label>
							<TextArea
								minRows={3}
								placeholder={t('talentReviews.v2.snapshot.create.descriptionPlaceholder')}
								value={formik.values.description}
								onChange={(description) => formik.setFieldValue('description', description)}
							/>
						</Box>
						{error &&
							(error.graphQLErrors.length ? (
								<GraphQLErrorMessage error={error.graphQLErrors[0]} />
							) : (
								<Paragraph color="danger">{t('talentReviews.v2.saveFailed')}</Paragraph>
							))}
					</Modal.Contents>
					<Modal.FormFooter
						disabled={loading}
						onCancel={props.onClose}
						onSave={() => formik.submitForm()}
					/>
				</FormikProvider>
			</Modal>
		</Popover>
	);
}
